// Web port of app/src/lib/groupMute.ts. Uses localStorage instead of
// AsyncStorage, so reads are synchronous — no hydrate() step needed.
// Per-group mute + optional DND window. State is local-only (not synced to
// Supabase); each device decides on its own what it plays out loud.

const KEY = 'hive.groupMute.v1';

export type GroupPrefs = {
  muted?: boolean;
  // "HH:mm" 24h. When both set and start !== end, messages are suppressed
  // while the current local time falls inside [start, end). start > end means
  // the window wraps midnight.
  dndStart?: string;
  dndEnd?: string;
};

type Prefs = Record<string, GroupPrefs>;

function load(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Prefs) : {};
  } catch {
    return {};
  }
}

let prefs: Prefs = load();
type Listener = (p: Prefs) => void;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((l) => l(prefs));
}

function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {}
  emit();
}

export function getGroupPrefs(groupId: string): GroupPrefs {
  return prefs[groupId] ?? {};
}

export function isGroupMuted(groupId: string): boolean {
  return !!prefs[groupId]?.muted;
}

function parseHHmm(s: string | undefined): number | null {
  if (!s) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

export function isInDND(groupId: string, now: Date = new Date()): boolean {
  const p = prefs[groupId];
  if (!p) return false;
  const start = parseHHmm(p.dndStart);
  const end = parseHHmm(p.dndEnd);
  if (start === null || end === null) return false;
  if (start === end) return false;
  const cur = now.getHours() * 60 + now.getMinutes();
  if (start < end) return cur >= start && cur < end;
  return cur >= start || cur < end;
}

export function setGroupMute(groupId: string, muted: boolean): void {
  prefs[groupId] = { ...(prefs[groupId] ?? {}), muted };
  persist();
}

export function setDND(
  groupId: string,
  dndStart: string | undefined,
  dndEnd: string | undefined
): void {
  prefs[groupId] = { ...(prefs[groupId] ?? {}), dndStart, dndEnd };
  persist();
}

export function onGroupMuteChange(l: Listener): () => void {
  listeners.add(l);
  l(prefs);
  return () => {
    listeners.delete(l);
  };
}
